import type { CustomField, ItemType, PasskeyCredential } from "../vaultPayload.js";
import type { Entry } from "./entries.js";

export const EXPORT_FORMAT = "passwordflow-export";
export const EXPORT_VERSION = 1;

export interface ExportedItem {
  readonly type: ItemType;
  readonly title: string;
  readonly folder: string;
  readonly favorite: boolean;
  readonly username: string;
  readonly password: string;
  readonly url: string;
  readonly notes: string;
  readonly totp: string;
  readonly expires: string;
  readonly fields: ReadonlyArray<CustomField>;
  /** Includes the private key: whoever holds the file can sign in with it. */
  readonly passkey: PasskeyCredential | null;
  readonly updatedAt: string;
}

export interface VaultExport {
  readonly fileName: string;
  readonly json: string;
  readonly count: number;
  /** Items that could not be decrypted and are missing from the file. */
  readonly skipped: number;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Plaintext JSON of the personal vault, sorted by folder and title. Team items are not included. */
export function buildVaultExport(entries: ReadonlyArray<Entry>, skipped: number, now: Date): VaultExport {
  const items: ExportedItem[] = entries
    .map((entry: Entry): ExportedItem => ({
      type: entry.data.type,
      title: entry.data.title,
      folder: entry.meta.folder,
      favorite: entry.meta.favorite,
      username: entry.data.username,
      password: entry.data.password,
      url: entry.data.url,
      notes: entry.data.notes,
      totp: entry.data.totp,
      expires: entry.data.expires,
      fields: entry.data.fields,
      passkey: entry.data.passkey,
      updatedAt: new Date(entry.meta.updatedAt).toISOString()
    }))
    .sort((a: ExportedItem, b: ExportedItem): number => a.folder.localeCompare(b.folder, "de") || a.title.localeCompare(b.title, "de"));
  const document = {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt: now.toISOString(),
    items
  };
  return {
    fileName: `passwordflow-export-${isoDate(now)}.json`,
    json: `${JSON.stringify(document, null, 2)}\n`,
    count: items.length,
    skipped
  };
}
